import { useState } from 'react';
import { ca } from '../i18n/ca';

type Command = (kind: string, payload: Record<string, unknown>) => Promise<unknown>;

/** A filled field as the number the runtime reads, divided as the field is shown (percent), or nothing. */
function numberOf(text: string, divide = 1): number | undefined {
  return text.trim() === '' ? undefined : Number(text) / divide;
}

/** The sky and the clock during the night (`env.set`): only what the director filled in is sent. */
export function EnvironmentCard({ live, command }: { live: boolean; command: Command }) {
  const t = ca.directionPage;
  const [weather, setWeather] = useState('');
  const [overcast, setOvercast] = useState('');
  const [fog, setFog] = useState('');
  const [hour, setHour] = useState('');
  const [multiplier, setMultiplier] = useState('');
  const payload = {
    weather: weather || undefined,
    overcast: numberOf(overcast, 100),
    fog: numberOf(fog, 100),
    hour: numberOf(hour),
    timeMultiplier: numberOf(multiplier),
  };
  const asked = Object.values(payload).some((v) => v !== undefined);
  return (
    <article className="card" aria-label={t.envTitle}>
      <h2>{t.envTitle}</h2>
      <p className="muted small">{t.envHint}</p>
      <form
        className="form"
        aria-label={t.envTitle}
        onSubmit={(e) => {
          e.preventDefault();
          void command('env.set', payload).then(() => {
            setWeather('');
            setOvercast('');
            setFog('');
            setHour('');
            setMultiplier('');
          });
        }}
      >
        <label>
          {ca.weather}
          <select value={weather} onChange={(e) => setWeather(e.target.value)}>
            <option value="">—</option>
            {Object.entries(ca.weatherNames).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <div className="two">
          <label>
            {t.envOvercast}
            <input
              type="number"
              min={0}
              max={100}
              value={overcast}
              onChange={(e) => setOvercast(e.target.value)}
            />
          </label>
          <label>
            {t.envFog}
            <input
              type="number"
              min={0}
              max={100}
              value={fog}
              onChange={(e) => setFog(e.target.value)}
            />
          </label>
          <label>
            {t.envHour}
            <input
              type="number"
              min={0}
              max={24}
              step={0.25}
              value={hour}
              onChange={(e) => setHour(e.target.value)}
            />
          </label>
          <label>
            {t.envMultiplier}
            <input
              type="number"
              min={0.1}
              max={120}
              step={0.1}
              value={multiplier}
              onChange={(e) => setMultiplier(e.target.value)}
            />
          </label>
        </div>
        <button type="submit" disabled={!live || !asked}>
          {t.envSend}
        </button>
      </form>
    </article>
  );
}

const POINT = '@point';

/**
 * An enemy reinforcement of a chosen size at an objective or at the point marked on the map
 * (`reinforce.enemy`), the same point a new objective takes.
 */
export function EnemyReinforcementCard({
  live,
  objectives,
  point,
  command,
}: {
  live: boolean;
  objectives: { id: string; title?: string | null }[];
  point: [number, number] | null;
  command: Command;
}) {
  const t = ca.directionPage;
  const [target, setTarget] = useState('');
  const [groups, setGroups] = useState('1');
  const ready = target === POINT ? point !== null : target !== '';
  return (
    <article className="card" aria-label={t.enemyTitle}>
      <h2>{t.enemyTitle}</h2>
      <p className="muted small">{t.enemyHint}</p>
      <form
        className="form"
        aria-label={t.enemyTitle}
        onSubmit={(e) => {
          e.preventDefault();
          void command(
            'reinforce.enemy',
            target === POINT
              ? { groups: Number(groups), position: point }
              : { groups: Number(groups), objectiveId: target },
          );
        }}
      >
        <div className="two">
          <label>
            {t.enemySize}
            <select value={groups} onChange={(e) => setGroups(e.target.value)}>
              {['1', '2', '3'].map((n) => (
                <option key={n} value={n}>
                  {t.enemyGroups(Number(n))}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t.enemyTarget}
            <select value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">—</option>
              <option value={POINT}>{t.enemyPoint}</option>
              {objectives.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.title ?? o.id}
                </option>
              ))}
            </select>
          </label>
        </div>
        {target === POINT && (
          <p className="muted small num">
            {point ? t.objectiveAt(point[0], point[1]) : t.objectivePositionHint}
          </p>
        )}
        <button type="submit" disabled={!live || !ready}>
          {t.enemySend}
        </button>
      </form>
    </article>
  );
}
